import { RadioGroup as RadixRadioGroup } from "radix-ui";
import { useId } from "react";
import type { ControlName } from "./Checkbox.tsx";
import { cx } from "./tokens.ts";

export type RadioOption<K extends string = string> = { value: K; label: string; description?: string; disabled?: boolean };

export type RadioGroupProps<K extends string = string> = ControlName & {
  /** The chosen option; undefined = none yet. */
  value: K | undefined;
  onChange: (value: K) => void;
  options: RadioOption<K>[];
  /** Default vertical (one option per row). */
  direction?: "vertical" | "horizontal";
  disabled?: boolean;
  /** The group; each option is `<testID>-<value>`. */
  testID: string;
};

/**
 * Choose one of a few options shown all at once, each with an optional description (notification level, who can
 * join). For 2–4 short options in a toolbar use `SegmentedControl`; for long lists, `Select`.
 */
export function RadioGroup<K extends string>({ value, onChange, options, label, accessibilityLabel, direction = "vertical", disabled, testID }: RadioGroupProps<K>) {
  const id = useId();
  return (
    <div className={cx("b-radio-group", disabled === true && "b-control-disabled")}>
      {label === undefined ? null : (
        <span id={`${id}-label`} className="b-field-label">
          {label}
        </span>
      )}
      <RadixRadioGroup.Root
        className={cx("b-radio-items", direction === "horizontal" && "b-radio-items-h")}
        value={value ?? ""}
        onValueChange={(v) => onChange(v as K)}
        orientation={direction}
        disabled={disabled}
        aria-labelledby={label === undefined ? undefined : `${id}-label`}
        aria-label={label === undefined ? accessibilityLabel : undefined}
        data-testid={testID}
      >
        {options.map((o) => {
          const itemId = `${id}-${o.value}`;
          return (
            <div key={o.value} className={cx("b-radio-row", o.disabled === true && "b-control-disabled")}>
              <RadixRadioGroup.Item
                id={itemId}
                value={o.value}
                disabled={o.disabled}
                className="b-radio"
                aria-describedby={o.description === undefined ? undefined : `${itemId}-description`}
                data-testid={`${testID}-${o.value}`}
              >
                <RadixRadioGroup.Indicator className="b-radio-dot" />
              </RadixRadioGroup.Item>
              <span className="b-radio-text">
                <label className="b-control-label" htmlFor={itemId}>
                  {o.label}
                </label>
                {o.description === undefined ? null : (
                  <span id={`${itemId}-description`} className="b-radio-description">
                    {o.description}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </RadixRadioGroup.Root>
    </div>
  );
}
